/**
 * JustificatifDepotFamille — item 12.3, côté FAMILLE (recette 20/08, issue 2).
 *
 * Le geste qui manquait : l'API de dépôt existait (routes `manage_own`, délai
 * commune opposable) mais aucun écran ne l'appelait — un parent ne pouvait pas
 * déposer son certificat en ligne. Ce panneau ferme la boucle :
 *
 *   · le menu ne propose QUE les absences encore justifiables (statut absent,
 *     dans le délai, sans dépôt existant) — la liste vient du serveur, qui
 *     applique les mêmes règles que le dépôt : le formulaire dit la vérité
 *     d'entrée, on ne saisit pas motif + pièce pour finir sur un refus ;
 *   · les dépôts passés s'affichent avec leur décision (à examiner / accepté /
 *     refusé + motif du staff) — la famille sait quoi refaire.
 *
 * Le staff décide ensuite depuis le Pointage (JustificatifsAbsencePanel) ;
 * accepter bascule l'absence en « justifiée » et la non-facturation réglée
 * par la commune s'applique toute seule.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type AbsenceJustificatif } from '@/lib/api';
import { Button, Badge, Alert } from '@/components/ui';
import { FileText, Upload } from 'lucide-react';

const SERVICES: Record<string, string> = {
  cantine: 'Cantine', periscolaire: 'Périscolaire', alsh: 'ALSH', adulte: 'Repas adulte',
};

const STATUTS: Record<AbsenceJustificatif['statut'], { l: string; v: 'warning' | 'success' | 'urgent' }> = {
  en_attente: { l: 'À examiner', v: 'warning' },
  accepte: { l: 'Accepté', v: 'success' },
  refuse: { l: 'Refusé', v: 'urgent' },
};

interface AbsenceJustifiable { cibleType: string; cibleId: string; dateService: string; personne: string }

function dateFr(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', weekday: 'short', day: 'numeric', month: 'short',
  }).format(new Date(`${iso}T12:00:00`));
}

export function JustificatifDepotFamille() {
  const [absences, setAbsences] = useState<AbsenceJustifiable[]>([]);
  const [miens, setMiens] = useState<AbsenceJustificatif[]>([]);
  const [cible, setCible] = useState('');
  const [motif, setMotif] = useState('');
  const [piece, setPiece] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        api.scolaire.justificatifs.justifiables(),
        api.scolaire.justificatifs.miens(),
      ]);
      setAbsences(a.absences); setMiens(m.justificatifs);
    } catch { /* panneau silencieux si l'un des deux échoue */ }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  async function deposer() {
    const abs = absences.find((a) => `${a.cibleType}:${a.cibleId}` === cible);
    if (!abs || !piece) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      await api.scolaire.justificatifs.deposer(
        { cibleType: abs.cibleType, cibleId: abs.cibleId, motif: motif.trim() }, piece);
      setMsg('Justificatif déposé — le service scolaire va l\u2019examiner.');
      setCible(''); setMotif(''); setPiece(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Dépôt impossible');
    } finally { setBusy(false); }
  }

  // Recette 20/08 (capture Henrico) : le titre de la section est affiché par
  // la page quoi qu'il arrive — se taire ici donnait une section qui se
  // dépliait sur du VIDE. On explique donc toujours le fonctionnement, même
  // quand il n'y a rien à justifier : l'écran dit comment l'absence arrive ici.

  const champ = 'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink';
  const label = 'mb-1 block text-xs text-soft';

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {absences.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs text-soft">
            Une absence (maladie, imprévu…) peut être justifiée en ligne dans le délai
            fixé par la mairie. Une fois le document accepté, le repas ou l\u2019accueil
            concerné n\u2019est pas facturé.
          </p>
          <div>
            <label className={label}>Absence concernée</label>
            <select value={cible} onChange={(e) => setCible(e.target.value)} className={champ}>
              <option value="">— Choisir —</option>
              {absences.map((a) => (
                <option key={`${a.cibleType}:${a.cibleId}`} value={`${a.cibleType}:${a.cibleId}`}>
                  {a.personne} — {dateFr(a.dateService)} — {SERVICES[a.cibleType] ?? a.cibleType}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Motif</label>
            <input type="text" value={motif} onChange={(e) => setMotif(e.target.value)}
              placeholder="Certificat médical, hospitalisation…" className={champ} maxLength={500} />
          </div>
          <div>
            <label className={label}>Document (PDF ou photo, 10 Mo max)</label>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setPiece(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink file:mr-3 file:rounded-lg file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-sm file:text-ink" />
          </div>
          <Button onClick={() => void deposer()}
            disabled={busy || !cible || motif.trim().length < 3 || !piece}>
            <Upload className="h-4 w-4" /> {busy ? 'Envoi…' : 'Déposer le justificatif'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-soft">
          Aucune absence à justifier pour le moment. Quand vous décochez un jour
          réservé (dans « Gérer les présences » d'un enfant, ou sur vos repas
          adulte), l'absence apparaît ici pendant le délai fixé par la mairie —
          vous pouvez alors déposer un certificat pour que ce jour ne soit pas
          facturé.
        </p>
      )}

      {miens.length > 0 && (
        <ul className="space-y-2 border-t border-border pt-3">
          {miens.map((j) => (
            <li key={j.id} className="flex flex-wrap items-center gap-2 text-sm text-ink">
              <FileText className="h-4 w-4 shrink-0 text-soft" aria-hidden="true" />
              <span>{j.personne ?? ''} — {dateFr(j.dateService)}</span>
              <Badge variant="neutral" size="sm">{SERVICES[j.cibleType] ?? j.cibleType}</Badge>
              <Badge variant={STATUTS[j.statut].v} size="sm">{STATUTS[j.statut].l}</Badge>
              {j.statut === 'refuse' && j.commentaire && (
                <span className="text-xs text-soft">Motif du refus : {j.commentaire}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
